import { tw } from "@careinfo/tailwind";
import React, { useCallback, useEffect, useState } from "react";
import DataTable from "../Reusable/DataTable";
import APIS from "../../constant/EndPoint";
import NACell from "../Reusable/DataTable/NACell";
import moment from "moment";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSessionStorage } from "@uidotdev/usehooks";
import { hasValues } from "../../utils/hasValues";
import { IFilterManifest } from "../../types/interface";
import Card from "../Reusable/Card";
import ManifestFilter from "./ManifestFilter";
import TabPanel from "../Reusable/Tab";
import { OrderList } from "../../pages/Consignment/ConsignmentList";

const VehicleManifestList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParms] = useSearchParams();
  const [localState, handleSetState] = useSessionStorage<IFilterManifest>(
    "dispatch-manifest-filter",
    {
      created__range: null,
      vehicle: null,
      driver: null,
      destination_branch: null,
    }
  );
  const [isFilterVisible, setFilterVisiable] = useState(hasValues(localState));
  const [active, setActive] = useState("");
  const { setValue, getValues, watch, reset, control } =
    useForm<IFilterManifest>({
      defaultValues: async () => {
        return {
          vehicle: localState?.vehicle ?? null,
          created__range: localState?.created__range ?? null,
          driver: localState?.driver ?? null,
          destination_branch: localState?.destination_branch ?? null,
        };
      },
    });

  const handleFilterReset = () => {
    reset({
      vehicle: null,
      created__range: null,
      driver: null,
      destination_branch: null,
    });
  };
  const checkedAllItems = searchParams.get("selectAllItems") === "1";

  const handleIndividualSelect = (value: string) => {
    let selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems || selectedItems === undefined) {
      searchParams.set("selectedItems", value);
      setSearchParms(searchParams);
      return;
    }
    let itemIndex = selectedItems?.findIndex((item) => item === value);
    if (itemIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length) {
        searchParams.set("selectedItems", newSelectedItems);
      } else {
        searchParams.delete("selectedItems");
      }
      searchParams.set("selectAllItems", "0");
      setSearchParms(searchParams);
    } else {
      selectedItems?.push(value);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      searchParams.set("selectedItems", newSelectedItems);
      setSearchParms(searchParams);
    }
  };

  const isSelected = (value: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems) {
      return false;
    }
    return selectedItems.includes(value);
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const isSelected = searchParams.get("selectAllItems");
    if (isSelected === "1") {
      searchParams.set("selectAllItems", "0");
      searchParams.delete("selectedItems");
      setSearchParms(searchParams);
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      searchParams.set("selectAllItems", "1");
      searchParams.set("selectedItems", selectedItems);
      setSearchParms(searchParams);
    }
  };

  const defaultColumns: any = [
    {
      header: "Manifest ID",
      accessorKey: "tracking_number",
      cell: NACell,
    },
    {
      header: "Created",
      accessorKey: "created",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>
              {getValue()
                ? moment(getValue()).format("YY-MM-DD HH:mm:ss")
                : "N/A"}
            </p>
          </>
        );
      },
    },
    {
      header: "Origin",
      accessorKey: "from_branch.name",
      cell: NACell,
    },
    {
      header: "Destination",
      accessorKey: "destination_branch.name",
      cell: NACell,
    },
    {
      header: "Vehicle",
      accessorKey: "vehicle.license_number",
      cell: NACell,
    },
    {
      header: "Driver",
      accessorKey: "driver.user",
      cell: NACell,
    },
    {
      header: "Total Items",
      accessorKey: "items.total",
      cell: ({ getValue }) => <p>{getValue() ?? "0"}</p>,
    },
    {
      header: "Missing Items",
      accessorKey: "items.missing",
      cell: ({ getValue }) => <p>{getValue() ?? "0"}</p>,
    },
    {
      header: "Received Items",
      accessorKey: "items.received",
      cell: ({ getValue }) => <p>{getValue() ?? "0"}</p>,
    },
  ];
  const handleDetailNavigate = (row: any) => {
    navigate(`edit/${row?.original?.id}`);
  };
  const filterParams = watch();

  useEffect(() => {
    handleSetState({
      ...localState,
      ...filterParams,
    });
  }, [filterParams]); //eslint-disable-line

  const params = useCallback(() => {
    const newValue = {};
    for (const [key, value] of Object.entries(filterParams)) {
      if (value) {
        if (Array.isArray(value)) {
          newValue[key] = value.map((v) => v.value).join(",");
        } else if (typeof value === "object") {
          newValue[key] = value.id;
        } else if (!value) {
          newValue[key] = null;
        } else {
          newValue[key] = value;
        }
      }
    }
    return newValue;
  }, [filterParams]);

  return (
    <>
      <Card title="Filters" onClick={() => setFilterVisiable(!isFilterVisible)}>
        <ManifestFilter
          isFilterVisible={isFilterVisible}
          setValue={setValue}
          getValues={getValues}
          reset={handleFilterReset}
          control={control}
        />
      </Card>
      <DataTable
        url={`${APIS.VEHICLE_MANIFEST}`}
        queryKey={"vehicle-manifest"}
        key={"manifest"}
        orderSearch={true}
        params={{
          ...params(),
        }}
        tableName="vehicle-manifest-list"
        handleDetailNavigate={handleDetailNavigate}
        columns={defaultColumns}
        addNewPagePath={"/create"}
      />
    </>
  );
};

export default VehicleManifestList;
